import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";

export default function ContactPage() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("sending");
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });
    try {
      const res = await fetch("https://readdy.ai/api/form/d81h9jabqqll9vhfdq6g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  const contactInfo = [
    {
      icon: "ri-map-pin-line",
      label: t("contact_location"),
      value: t("contact_address"),
      color: "from-amber-500 to-amber-600",
      href: "https://maps.google.com/?q=1401+Melrose+Place,Akwa,Douala,Cameroon",
    },
    {
      icon: "ri-whatsapp-line",
      label: "WhatsApp",
      value: t("contact_phone_val"),
      color: "from-green-400 to-emerald-500",
      href: "https://wa.me/237678012159",
    },
    {
      icon: "ri-mail-line",
      label: t("contact_email_us"),
      value: t("contact_email_val"),
      color: "from-amber-400 to-orange-500",
      href: "mailto:creativehub@gmail.com",
    },
    {
      icon: "ri-time-line",
      label: t("contact_hours"),
      value: t("contact_hours_val"),
      color: "from-stone-400 to-stone-500",
      href: null,
    },
  ];

  return (
    <div className="min-h-screen bg-[#f5ede0]">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 pb-16 bg-gradient-to-br from-amber-900 via-stone-800 to-amber-800 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=elegant%20gift%20boutique%20reception%20desk%20with%20flowers%20and%20candles%2C%20warm%20golden%20light%2C%20premium%20boutique%20interior%2C%20cream%20and%20rose%20tones%2C%20welcoming%20and%20beautiful&width=1920&height=400&seq=contact_hero&orientation=landscape"
            alt="Contact"
            className="w-full h-full object-cover object-top opacity-25"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center py-10">
          <span className="inline-block text-amber-300 text-xs font-semibold uppercase tracking-widest mb-3">— We'd Love to Hear From You —</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("contact_title")}
          </h1>
          <p className="text-white/70 text-lg">{t("contact_subtitle")}</p>
        </div>
      </div>

      <div ref={sectionRef} className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        {/* Contact Info Cards */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)" }}
        >
          {contactInfo.map((info, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-5 border border-amber-100 hover:-translate-y-1 transition-transform duration-300"
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br ${info.color} mb-4`}>
                <i className={`${info.icon} text-white text-lg`}></i>
              </div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{info.label}</p>
              {info.href ? (
                <a href={info.href} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-gray-800 hover:text-amber-700 transition-colors cursor-pointer leading-relaxed">
                  {info.value}
                </a>
              ) : (
                <p className="text-sm font-semibold text-gray-800 leading-relaxed">{info.value}</p>
              )}
            </div>
          ))}
        </div>

        {/* Form + Map */}
        <div
          className="grid lg:grid-cols-2 gap-10 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transitionDelay: "0.2s" }}
        >
          {/* Contact Form */}
          <div className="bg-white rounded-3xl p-8 border border-amber-100">
            <h2 className="text-2xl font-bold text-stone-800 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isFr ? "Envoyez-nous un message" : "Send Us a Message"}
            </h2>

            {formStatus === "success" ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-3xl text-green-500"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {isFr ? "Message envoyé!" : "Message Sent!"}
                </h3>
                <p className="text-gray-500 text-sm mb-6">
                  {isFr ? "Nous vous répondrons dans les 24 heures." : "We'll get back to you within 24 hours."}
                </p>
                <button
                  onClick={() => setFormStatus("idle")}
                  className="px-6 py-2.5 bg-gradient-to-r from-amber-700 to-amber-500 text-white text-sm font-semibold rounded-full hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  {isFr ? "Envoyer un autre" : "Send Another"}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col gap-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("contact_name")} *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-amber-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("contact_email")} *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-amber-300 transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("contact_phone")}</label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-amber-300 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("contact_subject")} *</label>
                  <input
                    type="text"
                    name="subject"
                    required
                    className="w-full px-4 py-3 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-amber-300 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{t("contact_message")} *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-amber-300 transition-colors resize-none"
                  />
                </div>

                {formStatus === "error" && (
                  <p className="text-red-500 text-xs">{isFr ? "Une erreur s'est produite. Réessayez." : "Something went wrong. Please try again."}</p>
                )}

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={formStatus === "sending"}
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-amber-700 to-amber-500 text-white font-semibold rounded-full hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {formStatus === "sending" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base"></i>
                        {isFr ? "Envoi..." : "Sending..."}
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-base"></i>
                        {t("contact_send")}
                      </>
                    )}
                  </button>
                  <a
                    href="https://wa.me/237678012159"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-green-500 text-white font-semibold rounded-full hover:bg-green-400 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-base"></i>
                    {t("contact_whatsapp")}
                  </a>
                </div>
              </form>
            )}
          </div>

          {/* Map + Info */}
          <div className="flex flex-col gap-6">
            {/* Google Map */}
            <div className="rounded-3xl overflow-hidden h-72 border border-amber-100">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3979.7!2d9.7!3d4.05!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sAkwa%2C+Rue+Malseuse%2C+Douala%2C+Cameroon!5e0!3m2!1sen!2scm!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Creative Hub Location"
              />
            </div>

            {/* Quick Contact */}
            <div className="bg-white rounded-3xl p-6 border border-amber-100">
              <h3 className="text-lg font-bold text-stone-800 mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isFr ? "Contact Rapide" : "Quick Contact"}
              </h3>
              <div className="flex flex-col gap-4">
                <a
                  href="https://wa.me/237678012159"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-green-50 rounded-2xl border border-green-100 hover:bg-green-100 transition-colors cursor-pointer group"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-green-500 flex-shrink-0">
                    <i className="ri-whatsapp-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm group-hover:text-green-700 transition-colors">WhatsApp</p>
                    <p className="text-gray-500 text-xs">{t("contact_phone_val")}</p>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a
                  href="mailto:creativehub@gmail.com"
                  className="flex items-center gap-4 p-4 bg-amber-50 rounded-2xl border border-amber-100 hover:bg-amber-100 transition-colors cursor-pointer group"
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 flex-shrink-0">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm group-hover:text-amber-700 transition-colors">{t("contact_email_us")}</p>
                    <p className="text-gray-500 text-xs">{t("contact_email_val")}</p>
                  </div>
                  <i className="ri-arrow-right-line text-gray-400 ml-auto group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}